import OperationAgentIcon from "@/assets/operation-facilities-agent.svg";
import StrategyAgentIcon from "@/assets/strategy-facilities-agent.svg";
import ExperienceAgentIcon from "@/assets/experience-facilities-agent.svg";
import WelcomeAgentIcon from "@/assets/welcome-facilities-agent.svg";
import HeaderAgentIcon from "@/assets/header-facilities-agent.svg";
import type { AgentTheme, AgentType } from "../types";

/**
 * Atalho que abre o agendamento de relatório pela Lia.
 *
 * O valor é o texto exato que o pre-filter `check_report_schedule` do agente reconhece —
 * é um contrato com o backend, não um rótulo de UI. Exportado para o tracking identificar
 * o atalho sem repetir o literal.
 */
export const REPORT_SCHEDULING_ACTION = "Agendamento de relatório";

/**
 * Registry central dos agentes IA.
 * Tudo que muda por agente (nome, ícone, cor, atalhos) vive aqui.
 *
 * Operação: paleta aiBlue (azul, já existente no projeto).
 * Estratégia: paleta agentStrategy (roxa).
 * Experiência: placeholder até design definir o ícone/cor.
 */
export const AGENT_THEMES: Record<AgentType, AgentTheme> = {
  operation: {
    type: "operation",
    nameKey: "chatbot-agent-operation-name",
    descriptionKey: "chatbot-agent-operation-description",
    iconSrc: OperationAgentIcon,
    colors: {
      bubble: "bg-aiBlue-100",
      accent: "bg-aiBlue-500",
      text: "text-aiBlue-800",
      border: "border-aiBlue-300",
      headerBg: "bg-agent-operation-radial",
    },
    // Operação: execução, tarefas, dia a dia.
    // Reusa os action IDs dos templates de greeting (chatbotUtils) para
    // preservar tracking e roteamento via intent_router. Chamado/OS são
    // mensagem livre ao agente (sem action de backend dedicada).
    shortcuts: [
      {
        textKey: "chatbot-add-members",
        action: "action_do_for_me_invite",
        category: "members",
      },
      {
        textKey: "chatbot-remove-members",
        action: "action_do_for_me",
        category: "members",
      },
      {
        textKey: "chatbot-suggestion-import-members",
        action: "importacao_membros",
        category: "gestao-relatorios",
      },
      {
        textKey: "chatbot-suggestion-booking",
        action: "reservas_realizar",
        category: "espacos-reservas",
      },
      {
        textKey: "chatbot-shortcut-tickets-list",
        action: "Acompanhar chamado",
      },
      {
        textKey: "chatbot-shortcut-open-ticket",
        action: "Abrir e acompanhar ordem de serviço",
      },
    ],
  },
  estrategy: {
    type: "estrategy",
    nameKey: "chatbot-agent-estrategy-name",
    descriptionKey: "chatbot-agent-estrategy-description",
    iconSrc: StrategyAgentIcon,
    colors: {
      bubble: "bg-agentStrategy-100",
      accent: "bg-agentStrategy-500",
      text: "text-agentStrategy-800",
      border: "border-agentStrategy-300",
      headerBg: "bg-agent-strategy-radial",
    },
    // Estratégia: dados, configurações, decisão.
    // Reusa action IDs dos templates de greeting (chatbotUtils).
    shortcuts: [
      {
        // Texto exato reconhecido pelo pre-filter `check_report_schedule` do agente,
        // que roteia para o intent `report_schedule_config`.
        textKey: "chatbot-shortcut-report-schedule",
        action: REPORT_SCHEDULING_ACTION,
        badgeKey: "chatbot-shortcut-badge-new",
      },
      {
        textKey: "chatbot-change-cost-center",
        action: "action_do_for_me_cost_center",
        category: "members",
      },
      {
        textKey: "chatbot-change-member-plan",
        action: "action_do_for_me_update_plan",
        category: "members",
      },
      {
        textKey: "chatbot-suggestion-checkin-usage",
        action: "checkin_realizar",
        category: "espacos-reservas",
      },
    ],
  },
  experience: {
    type: "experience",
    nameKey: "chatbot-agent-experience-name",
    descriptionKey: "chatbot-agent-experience-description",
    iconSrc: ExperienceAgentIcon,
    colors: {
      bubble: "bg-agentExperience-100",
      accent: "bg-agentExperience-500",
      text: "text-agentExperience-800",
      border: "border-agentExperience-300",
      headerBg: "bg-agent-experience-radial",
    },
    // Experiência: insights sobre a vivência de uso dos espaços.
    shortcuts: [
      {
        textKey: "chatbot-shortcut-usage-reasons-insights",
        action: "motivos_de_uso_insights",
        category: "gestao-relatorios",
      },
    ],
  },
};

const DEFAULT_AGENT_TYPE: AgentType = "operation";

export function getAgentTheme(type?: AgentType | null): AgentTheme {
  if (!type) return AGENT_THEMES[DEFAULT_AGENT_TYPE];
  return AGENT_THEMES[type] ?? AGENT_THEMES[DEFAULT_AGENT_TYPE];
}

/**
 * Resolve o agente dono de um `action` a partir dos shortcuts do registry.
 * Usado para forçar o agent type quando a action é disparada fora do hub
 * picker (ex.: via suggestion/greeting), preservando o mesmo padrão do
 * `handleHubShortcutClick`. Retorna undefined quando nenhum agente reivindica
 * a action (mensagem livre → classificador decide).
 */
export function getAgentTypeForAction(
  action?: string | null,
): AgentType | undefined {
  if (!action) return undefined;
  for (const theme of Object.values(AGENT_THEMES)) {
    if (theme.shortcuts.some((shortcut) => shortcut.action === action)) {
      return theme.type;
    }
  }
  return undefined;
}

export { WelcomeAgentIcon, HeaderAgentIcon };
